/**
 * PracticeHeatmap — Pitch zone heatmap + path line visualization
 *
 * Fetches aggregated DiamondVision data from /api/baseball/heatmap-data/:gameId
 * and renders a 5×5 strike zone grid with scatter overlay + movement path lines.
 *
 * Two view modes:
 *  1. Strike Zone (5×5 grid) — pitch frequency / velocity / strike% by zone
 *  2. Path Lines — pitcher movement trajectories across segments
 *
 * Props:
 *   gameId     — required game ID for data fetch
 *   onPitchClick — optional callback when a scatter dot is clicked
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Flame,
  Route,
  RefreshCw,
  Loader2,
  AlertCircle,
  Target,
  Zap,
  BarChart3,
} from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { CAROLINA_BLUE } from '../../config/uncConstants';

// ── Heat color scale (cold→hot) ───────────────────────────────────────────
const HEAT_SCALE = [
  { t: 0, color: [59, 130, 246] },   // Blue — cold
  { t: 0.2, color: [6, 182, 212] },  // Cyan
  { t: 0.4, color: [34, 197, 94] },  // Green
  { t: 0.6, color: [234, 179, 8] },  // Gold
  { t: 0.8, color: [249, 115, 22] }, // Orange
  { t: 1.0, color: [239, 68, 68] },  // Red — hot
];

function interpolateHeat(normalized) {
  const n = Math.max(0, Math.min(1, normalized));
  for (let i = HEAT_SCALE.length - 1; i > 0; i--) {
    if (n >= HEAT_SCALE[i - 1].t) {
      const lo = HEAT_SCALE[i - 1];
      const hi = HEAT_SCALE[i];
      const f = (n - lo.t) / (hi.t - lo.t);
      const r = Math.round(lo.color[0] + (hi.color[0] - lo.color[0]) * f);
      const g = Math.round(lo.color[1] + (hi.color[1] - lo.color[1]) * f);
      const b = Math.round(lo.color[2] + (hi.color[2] - lo.color[2]) * f);
      return `rgba(${r},${g},${b}`;
    }
  }
  return `rgba(${HEAT_SCALE[0].color.join(',')}`;
}

// ── Pitch type colors ──────────────────────────────────────────────────────
const PITCH_COLORS = {
  FB: '#EF4444',  // Fastball — red
  SL: '#3B82F6',  // Slider — blue
  CB: '#8B5CF6',  // Curveball — purple
  CH: '#22C55E',  // Changeup — green
  CT: '#F59E0B',  // Cutter — amber
  SI: '#EC4899',  // Sinker — pink
  KN: '#6B7280',  // Knuckle — gray
  UNK: '#9CA3AF', // Unknown
};

const PITCH_TYPE_LABELS = {
  ALL: 'All Pitches',
  FB: 'Fastball',
  SL: 'Slider',
  CB: 'Curveball',
  CH: 'Changeup',
  CT: 'Cutter',
  SI: 'Sinker',
};

const METRIC_OPTIONS = [
  { id: 'frequency', label: 'Frequency', icon: BarChart3 },
  { id: 'velocity', label: 'Avg Velocity', icon: Zap },
  { id: 'result', label: 'Strike %', icon: Target },
];

// ── Path line colors ───────────────────────────────────────────────────────
const PATH_COLORS = [CAROLINA_BLUE, CAROLINA_BLUE, '#FF6B35', '#4ECDC4', '#7B68EE', '#FF4444'];

// ── SVG dimensions ─────────────────────────────────────────────────────────
const SVG_W = 300;
const SVG_H = 340;
const ZONE_PADDING = 20;
const ZONE_SIZE = SVG_W - ZONE_PADDING * 2; // 260
const GRID = 5;
const CELL = ZONE_SIZE / GRID; // 52

export default function PracticeHeatmap({ gameId, onPitchClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pitchType, setPitchType] = useState('ALL');
  const [metric, setMetric] = useState('frequency');
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showPaths, setShowPaths] = useState(true);
  const [showScatter, setShowScatter] = useState(true);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [hoveredPitch, setHoveredPitch] = useState(null);

  // ── Fetch heatmap data ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/baseball/heatmap-data/${encodeURIComponent(gameId)}?pitchType=${pitchType}&metric=${metric}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json.data || json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [gameId, pitchType, metric]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived values ─────────────────────────────────────────────────────
  const maxZoneValue = useMemo(() => {
    if (!data?.zones) return 1;
    return Math.max(1, ...Object.values(data.zones));
  }, [data]);

  const pitchTypesPresent = useMemo(() => {
    if (!data?.pitchLocations) return ['ALL'];
    const types = new Set(data.pitchLocations.map(p => p.pitchType));
    return ['ALL', ...Array.from(types).sort()];
  }, [data]);

  // ── Metric label for zone tooltip ──────────────────────────────────────
  const metricSuffix = metric === 'velocity' ? ' mph' : metric === 'result' ? '%' : '';

  // ── Empty / loading / error states ─────────────────────────────────────
  if (!gameId) {
    return (
      <div
        className="flex items-center justify-center h-48 rounded-xl"
        style={{ background: C.s1, border: `1px solid ${C.border}` }}
      >
        <p className="text-xs" style={{ color: C.t3 }}>Select a game to view pitch heatmap</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64 rounded-xl"
        style={{ background: C.s1, border: `1px solid ${C.border}` }}
      >
        <Loader2 className="animate-spin mr-2" size={16} style={{ color: CAROLINA_BLUE }} />
        <span className="text-xs" style={{ color: C.t2 }}>Loading heatmap data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center h-48 rounded-xl gap-2"
        style={{ background: C.s1, border: `1px solid ${C.border}` }}
      >
        <AlertCircle size={14} className="text-red-400" />
        <span className="text-xs text-red-400">{error}</span>
        <button
          onClick={fetchData}
          className="ml-2 px-2 py-1 rounded text-[10px] font-medium"
          style={{ background: C.s2, color: C.t2, border: `1px solid ${C.border}` }}
        >
          Retry
        </button>
      </div>
    );
  }

  const zones = data?.zones || {};
  const pitchLocs = data?.pitchLocations || [];
  const paths = data?.pathSegments || [];

  return (
    <div className="space-y-3">
      {/* ── Header + controls ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="text-xs font-semibold" style={{ color: C.text }}>
            Pitch Zone Heatmap
          </h4>
          <p className="text-[10px] mt-0.5" style={{ color: C.t3 }}>
            {data?.totalPitches || 0} pitches · {data?.segmentsAnalyzed || 0} segments analyzed
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Layer toggles */}
          <LayerToggle active={showHeatmap} onClick={() => setShowHeatmap(!showHeatmap)} icon={Flame} label="Zones" color={CAROLINA_BLUE} />
          <LayerToggle active={showScatter} onClick={() => setShowScatter(!showScatter)} icon={Target} label="Dots" color={CAROLINA_BLUE} />
          <LayerToggle active={showPaths} onClick={() => setShowPaths(!showPaths)} icon={Route} label="Paths" color="#4ECDC4" />
          <button
            onClick={fetchData}
            className="p-1.5 rounded-md transition-colors"
            style={{ background: C.s2, color: C.t3, border: `1px solid ${C.border}` }}
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* ── Filters row ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Pitch type pills */}
        <div className="flex items-center gap-1">
          {pitchTypesPresent.map(pt => (
            <button
              key={pt}
              onClick={() => setPitchType(pt)}
              className="px-2 py-0.5 rounded-full text-[10px] font-medium transition-all"
              style={{
                background: pitchType === pt ? (PITCH_COLORS[pt] || CAROLINA_BLUE) + '22' : C.s2,
                color: pitchType === pt ? (PITCH_COLORS[pt] || CAROLINA_BLUE) : C.t3,
                border: `1px solid ${pitchType === pt ? (PITCH_COLORS[pt] || CAROLINA_BLUE) + '55' : C.border}`,
              }}
            >
              {PITCH_TYPE_LABELS[pt] || pt}
            </button>
          ))}
        </div>

        {/* Metric selector */}
        <div className="flex items-center gap-1 ml-auto">
          {METRIC_OPTIONS.map(m => {
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                onClick={() => setMetric(m.id)}
                className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium transition-all"
                style={{
                  background: metric === m.id ? CAROLINA_BLUE + '22' : C.s2,
                  color: metric === m.id ? CAROLINA_BLUE : C.t3,
                  border: `1px solid ${metric === m.id ? CAROLINA_BLUE + '55' : C.border}`,
                }}
              >
                <Icon size={10} /> {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── SVG Strike Zone ────────────────────────────────────────────── */}
      <div
        className="rounded-xl overflow-hidden relative"
        style={{ background: C.s1, border: `1px solid ${C.border}` }}
      >
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          className="w-full"
          style={{ maxWidth: 500, margin: '0 auto', display: 'block' }}
        >
          {/* Background */}
          <rect x="0" y="0" width={SVG_W} height={SVG_H} fill={C.bg || '#000'} />

          {/* Strike zone outline */}
          <rect
            x={ZONE_PADDING}
            y={ZONE_PADDING}
            width={ZONE_SIZE}
            height={ZONE_SIZE}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="1.5"
            rx="2"
          />

          {/* Grid lines */}
          {Array.from({ length: GRID - 1 }).map((_, i) => (
            <g key={`grid-${i}`}>
              <line
                x1={ZONE_PADDING + CELL * (i + 1)}
                y1={ZONE_PADDING}
                x2={ZONE_PADDING + CELL * (i + 1)}
                y2={ZONE_PADDING + ZONE_SIZE}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="0.5"
              />
              <line
                x1={ZONE_PADDING}
                y1={ZONE_PADDING + CELL * (i + 1)}
                x2={ZONE_PADDING + ZONE_SIZE}
                y2={ZONE_PADDING + CELL * (i + 1)}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="0.5"
              />
            </g>
          ))}

          {/* ── Heatmap zones ───────────────────────────────────────── */}
          {showHeatmap && Array.from({ length: GRID * GRID }).map((_, idx) => {
            const row = Math.floor(idx / GRID);
            const col = idx % GRID;
            const key = `${row}-${col}`;
            const val = zones[key] || 0;
            const normalized = val / maxZoneValue;
            const isHovered = hoveredZone === key;

            const rgbBase = interpolateHeat(normalized);
            const alpha = val > 0 ? 0.15 + normalized * 0.55 : 0.02;

            return (
              <rect
                key={key}
                x={ZONE_PADDING + col * CELL}
                y={ZONE_PADDING + row * CELL}
                width={CELL}
                height={CELL}
                fill={`${rgbBase},${alpha})`}
                stroke={isHovered ? CAROLINA_BLUE : 'transparent'}
                strokeWidth={isHovered ? 1.5 : 0}
                style={{ cursor: 'pointer', transition: 'fill 0.2s' }}
                onMouseEnter={() => setHoveredZone(key)}
                onMouseLeave={() => setHoveredZone(null)}
              />
            );
          })}

          {/* Zone value labels */}
          {showHeatmap && Array.from({ length: GRID * GRID }).map((_, idx) => {
            const row = Math.floor(idx / GRID);
            const col = idx % GRID;
            const key = `${row}-${col}`;
            const val = zones[key] || 0;
            if (val === 0) return null;

            return (
              <text
                key={`lbl-${key}`}
                x={ZONE_PADDING + col * CELL + CELL / 2}
                y={ZONE_PADDING + row * CELL + CELL / 2}
                textAnchor="middle"
                dominantBaseline="central"
                fill="rgba(255,255,255,0.9)"
                fontSize="9"
                fontWeight="700"
                fontFamily="monospace"
                style={{ pointerEvents: 'none' }}
              >
                {metric === 'velocity' ? val.toFixed(1) : Math.round(val)}
              </text>
            );
          })}

          {/* ── Scatter overlay ──────────────────────────────────────── */}
          {showScatter && pitchLocs.slice(0, 80).map((p, i) => {
            const cx = ZONE_PADDING + p.x * ZONE_SIZE;
            const cy = ZONE_PADDING + p.y * ZONE_SIZE;
            const color = PITCH_COLORS[p.pitchType] || PITCH_COLORS.UNK;
            const isHov = hoveredPitch === i;

            return (
              <circle
                key={`dot-${i}`}
                cx={cx}
                cy={cy}
                r={isHov ? 5 : 3}
                fill={color}
                fillOpacity={isHov ? 1 : 0.7}
                stroke={isHov ? '#fff' : 'rgba(0,0,0,0.4)'}
                strokeWidth={isHov ? 1.5 : 0.5}
                style={{ cursor: 'pointer', transition: 'r 0.15s, fill-opacity 0.15s' }}
                onMouseEnter={() => setHoveredPitch(i)}
                onMouseLeave={() => setHoveredPitch(null)}
                onClick={() => onPitchClick?.(p)}
              />
            );
          })}

          {/* ── Path lines ──────────────────────────────────────────── */}
          {showPaths && paths.length > 1 && (() => {
            // Sort path segments by time and draw connected lines
            const sorted = [...paths].sort((a, b) => a.startSec - b.startSec);
            const points = sorted.map(s => ({
              x: ZONE_PADDING + (s.x / 100) * ZONE_SIZE,
              y: ZONE_PADDING + (s.y / 100) * ZONE_SIZE,
              title: s.title,
            }));

            // Group by segment for multi-path rendering
            const segmentGroups = {};
            for (const s of sorted) {
              const key = s.segment ?? 0;
              if (!segmentGroups[key]) segmentGroups[key] = [];
              segmentGroups[key].push({
                x: ZONE_PADDING + (s.x / 100) * ZONE_SIZE,
                y: ZONE_PADDING + (s.y / 100) * ZONE_SIZE,
                title: s.title,
              });
            }

            return Object.entries(segmentGroups).map(([seg, pts], groupIdx) => {
              if (pts.length < 2) return null;
              const color = PATH_COLORS[groupIdx % PATH_COLORS.length];
              const d = pts.map((p, j) => `${j === 0 ? 'M' : 'L'} ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

              return (
                <g key={`path-${seg}`}>
                  {/* Shadow */}
                  <path d={d} fill="none" stroke="rgba(0,0,0,0.5)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                  {/* Main line */}
                  <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
                  {/* Start dot */}
                  <circle cx={pts[0].x} cy={pts[0].y} r="3" fill={color} stroke="#000" strokeWidth="0.5" />
                  {/* End dot */}
                  <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="2.5" fill="#000" stroke={color} strokeWidth="1.5" />
                </g>
              );
            });
          })()}

          {/* ── Legend labels (zones) ────────────────────────────────── */}
          <text x={SVG_W / 2} y={ZONE_PADDING + ZONE_SIZE + 16} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8" fontWeight="500">
            Catcher&apos;s View — Inside ← → Outside
          </text>
          <text x={8} y={ZONE_PADDING + ZONE_SIZE / 2} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" transform={`rotate(-90, 8, ${ZONE_PADDING + ZONE_SIZE / 2})`}>
            High ↑
          </text>
          <text x={SVG_W - 8} y={ZONE_PADDING + ZONE_SIZE / 2} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" transform={`rotate(90, ${SVG_W - 8}, ${ZONE_PADDING + ZONE_SIZE / 2})`}>
            Low ↓
          </text>
        </svg>

        {/* ── Hover tooltip ──────────────────────────────────────────── */}
        {hoveredZone && zones[hoveredZone] !== undefined && (
          <div
            className="absolute top-3 left-3 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(0,0,0,0.92)', border: `1px solid ${C.border}`, zIndex: 10 }}
          >
            <span className="text-[10px] font-mono" style={{ color: C.t3 }}>Zone {hoveredZone}: </span>
            <span className="text-[10px] font-mono font-bold text-white">
              {metric === 'velocity' ? (zones[hoveredZone] || 0).toFixed(1) : Math.round(zones[hoveredZone] || 0)}{metricSuffix}
            </span>
          </div>
        )}

        {/* ── Pitch hover tooltip ────────────────────────────────────── */}
        {hoveredPitch !== null && pitchLocs[hoveredPitch] && (
          <div
            className="absolute top-3 right-3 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(0,0,0,0.92)', border: `1px solid ${C.border}`, zIndex: 10 }}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ background: PITCH_COLORS[pitchLocs[hoveredPitch].pitchType] || PITCH_COLORS.UNK }}
              />
              <span className="text-[10px] font-medium text-white">
                {PITCH_TYPE_LABELS[pitchLocs[hoveredPitch].pitchType] || pitchLocs[hoveredPitch].pitchType}
              </span>
            </div>
            {pitchLocs[hoveredPitch].velocity && (
              <div className="text-[10px] font-mono" style={{ color: C.t2 }}>
                {pitchLocs[hoveredPitch].velocity} mph
              </div>
            )}
            <div className="text-[10px]" style={{ color: C.t3 }}>
              {pitchLocs[hoveredPitch].result}
            </div>
          </div>
        )}
      </div>

      {/* ── Pitch type legend ──────────────────────────────────────────── */}
      {showScatter && pitchLocs.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          {pitchTypesPresent.filter(t => t !== 'ALL').map(pt => (
            <div key={pt} className="flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ background: PITCH_COLORS[pt] || PITCH_COLORS.UNK }}
              />
              <span className="text-[10px]" style={{ color: C.t3 }}>
                {PITCH_TYPE_LABELS[pt] || pt}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Reusable layer toggle button ───────────────────────────────────────────
function LayerToggle({ active, onClick, icon: Icon, label, color }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all"
      style={{
        background: active ? color + '22' : C.s2,
        color: active ? color : C.t3,
        border: `1px solid ${active ? color + '55' : C.border}`,
      }}
    >
      <Icon size={10} /> {label}
    </button>
  );
}
